import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './styles.css';

const Projects = () => {
  // État pour suivre les images actuellement affichées par projet
  const [visibleImages, setVisibleImages] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Déterminer si l'appareil est mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Vérifier au chargement et à chaque redimensionnement
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Nombre d'images à afficher selon l'appareil
  const imagesPerPage = isMobile ? 1 : 4;

  // Données d'exemple pour les projets
  const projects = [
    {
      id: 1,
      title: "Rénovation complète à Marseille",
      description: "Nous avons entièrement rénové cette piscine en modernisant son revêtement et ses équipements pour un résultat esthétique et durable.",
      images: ["projet1-image1.jpeg", "projet1-image2.jpeg", "projet1-image3.jpeg", "projet1-image4.jpeg", "projet1-image5.jpeg"]
    },
    {
      id: 2,
      title: "Modernisation à Aix-en-Provence",
      description: "Remplacement du système de filtration et installation d'éclairages LED pour une piscine plus performante et économique.",
      images: ["projet2-image1.jpeg", "projet2-image2.jpeg", "projet2-image3.jpeg", "projet2-image4.jpeg", "projet2-image5.jpeg"]
    },
    {
      id: 3,
      title: "Réparation à Cassis",
      description: "Intervention sur l'étanchéité et réparation des équipements pour remettre ce bassin en parfait état de fonctionnement.",
      images: ["projet3-image1.jpeg", "projet3-image2.jpeg", "projet3-image3.jpeg", "projet3-image4.jpeg", "projet3-image5.jpeg", 
               "projet3-image6.jpeg", "projet3-image7.jpeg", "projet3-image8.jpeg", "projet3-image9.jpeg"]
    },
    {
      id: 4,
      title: "Autres chantiers",
      description: "Nous réalisons de nombreux projets dans tout le département des Bouches-du-Rhône : rénovations, réparations et améliorations pour des piscines adaptées à vos besoins.",
      images: ["projet4-image1.jpeg", "projet4-image2.jpeg", "projet4-image3.jpeg", "projet4-image4.jpeg"]
    }
  ];

  // Fonction pour naviguer vers la page suivante d'images
  const nextImages = (projectId) => {
    setVisibleImages(prev => {
      const currentStart = prev[projectId] || 0;
      const projectImages = projects.find(p => p.id === projectId).images;
      const nextStart = Math.min(currentStart + imagesPerPage, projectImages.length - imagesPerPage);
      return { ...prev, [projectId]: nextStart };
    });
  };

  // Fonction pour naviguer vers la page précédente d'images
  const prevImages = (projectId) => {
    setVisibleImages(prev => {
      const currentStart = prev[projectId] || 0;
      const prevStart = Math.max(currentStart - imagesPerPage, 0);
      return { ...prev, [projectId]: prevStart };
    });
  };

  // Fonction pour obtenir les images actuellement visibles pour un projet
  const getVisibleImages = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    const start = visibleImages[projectId] || 0;
    return project.images.slice(start, start + imagesPerPage);
  };

  // Vérifier si un projet a des images supplémentaires après les images actuellement visibles
  const hasMoreImages = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    const start = visibleImages[projectId] || 0;
    return start + imagesPerPage < project.images.length;
  };

  // Vérifier si un projet a des images précédentes avant les images actuellement visibles
  const hasPrevImages = (projectId) => {
    const start = visibleImages[projectId] || 0;
    return start > 0;
  };

  // Ouvrir une image en plein écran
  const openLightbox = (image) => {
    setLightboxImage(image);
    document.body.style.overflow = 'hidden'; // Empêcher le défilement de la page
  };

  // Fermer le mode plein écran
  const closeLightbox = () => {
    setLightboxImage(null);
    document.body.style.overflow = 'auto'; // Rétablir le défilement de la page
  };
  
  // Fonction pour basculer l'état du menu mobile
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };
  
  // Fonction pour fermer le menu lors d'un clic sur un lien
  const closeMenu = () => {
    setMenuOpen(false);
  };

  return (
    <div className="page-container">
      <header className="header">
        <div className="logo">
          <h1>RENOV-PISCINES 13</h1>
        </div>
        <nav className={`navigation ${menuOpen ? 'mobile-menu-open' : ''}`}>
          <ul>
            <li><Link to="/" onClick={closeMenu}>Accueil</Link></li>
            <li><Link to="/projets" className="active" onClick={closeMenu}>Nos précédents chantiers</Link></li>
            <li><Link to="/contact" onClick={closeMenu}>Nous contacter</Link></li>
          </ul>
        </nav>
        <div className={`hamburger-menu ${menuOpen ? 'open' : ''}`} onClick={toggleMenu}>
          <div className="bar"></div>
          <div className="bar"></div>
          <div className="bar"></div>
        </div>
      </header>

      <main className="projects-main">
        <section className="projects-hero">
          <h2>Nos précédents chantiers</h2>
          <p>Découvrez nos réalisations et laissez-vous inspirer pour votre projet de rénovation</p>
        </section>

        <section className="projects-gallery">
          {projects.map(project => (
            <div className="project-card" key={project.id}>
              <h3>{project.title}</h3>
              <p>{project.description}</p>
              
              <div className="project-gallery-container">
                {/* Flèche précédente */}
                {hasPrevImages(project.id) && (
                  <button 
                    className="gallery-nav-button prev-button"
                    onClick={() => prevImages(project.id)}
                    aria-label="Image précédente"
                  >
                    &lt;
                  </button>
                )}
                
                {/* Images du projet */}
                <div className="project-images">
                  {getVisibleImages(project.id).map((image, index) => (
                    <div className="project-image-container" key={index}>
                      <img 
                        src={`/${image}`} 
                        alt={`${project.title} - Photo ${index + 1}`}
                        className="project-image"
                        onClick={() => openLightbox(image)}
                      />
                    </div>
                  ))}
                </div>
                
                {/* Flèche suivante si plus d'images */}
                {hasMoreImages(project.id) && (
                  <button 
                    className="gallery-nav-button next-button"
                    onClick={() => nextImages(project.id)}
                  >
                    &gt;
                  </button>
                )}
              </div>
              
            </div>
          ))}
        </section>

        {/* Lightbox pour affichage en plein écran */}
        {lightboxImage && (
          <div className="lightbox" onClick={closeLightbox}>
            <div className="lightbox-content">
              <img src={`/${lightboxImage}`} alt="Vue agrandie" className="lightbox-image" />
              <button className="lightbox-close" onClick={closeLightbox}>
                &times;
              </button>
            </div>
          </div>
        )}

        <section className="cta-section">
          <div className="cta-content">
            <h2>Vous avez un projet de rénovation ?</h2>
            <p>Contactez-nous dès aujourd'hui pour obtenir un devis personnalisé.</p>
            <Link to="/contact" className="btn-primary">Nous contacter</Link>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>RENOV-PISCINES 13</h3>
            <p>Votre spécialiste de la rénovation de piscines dans les Bouches-du-Rhône.</p>
          </div>
          <div className="footer-section">
            <h3>Contact</h3>
            <p>Email: renovpiscines13@outlook.fr</p>
            <p>Téléphone: 06 51 51 29 93</p>
          </div>
          <div className="footer-section">
            <h3>Horaires</h3>
            <p>Lundi - Vendredi: 8h - 19h</p>
            <p>Samedi: 8h - 12h</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 RENOV-PISCINES 13. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default Projects;